// The browser client, not the server one: Oak imports modules out of this
// directory directly, so a server-client import here would break its build.
import { StateOfMatter, UnitType } from "encamp-prisma/generated/browser";

import { STATES, StateAbbreviation } from "../constants/states";

/**
 * The single definition of which units a jurisdiction permits on a
 * `ReportingFacilityChemical` and on its `ReportingFacilityChemicalStorageLocation`
 * children.
 *
 * These values used to sit inline inside the rule closures of
 * encamp-compliance-engine's EPCRA schemas
 * (`reportingFacilityChemicalSchema.ts`, `reportingFacilityChemicalStorageLocationSchema.ts`).
 * They live here now, and the compliance engine imports them, because
 * encamp-compliance-engine depends on encamp-shared and not the other way
 * round — so this is the only package that both the compliance engine and the
 * staff reporting-unit override predicate (`canApplyReportingUnitOverride`,
 * called by Oak's dialog and by the `setReportingUnitOverride` resolver) can
 * reach. Nothing restates them; a change here changes both the validation
 * issue a customer sees and the units staff are offered.
 *
 * The regulatory basis is unchanged by the move: these are the units each
 * state's Tier II portal will accept in the reported-amount fields, which is
 * why every state but CA/OR/SD is pounds-only.
 */

/**
 * Jurisdictions whose RFC unit rule is anything other than pounds-only. The
 * compliance engine scopes its default pounds-only rule with
 * `allStatesExcept([...this])`, and `allowedReportingFacilityChemicalUnits`
 * dispatches on the same set, so the two cannot cover different states.
 */
export const STATES_WITH_NON_POUNDS_REPORTING_UNITS = ["CA", "OR", "SD"] as const;

/** Every state except CA, OR and SD: the reported amount must be in pounds. */
export const POUNDS_ONLY_REPORTING_UNITS: UnitType[] = [UnitType.POUNDS];

/** CERS accepts any of these four for a reported amount. */
export const CA_ALLOWED_REPORTING_UNITS: UnitType[] = [
  UnitType.POUNDS,
  UnitType.GALLONS,
  UnitType.TONS,
  UnitType.CUBIC_FEET,
];

export const SD_ALLOWED_REPORTING_UNITS: UnitType[] = [UnitType.POUNDS, UnitType.GALLONS];

/**
 * Oregon varies by the chemical's state of matter rather than being a fixed
 * list. An unknown state of matter yields an empty list — the same answer the
 * compliance engine's own rule produces, which is what makes it an issue on
 * every unit rather than a silent pass.
 */
export function oregonAllowedReportingUnits(
  stateOfMatter: readonly StateOfMatter[] | null | undefined
): UnitType[] {
  if (stateOfMatter?.includes(StateOfMatter.SOLID)) {
    return [UnitType.POUNDS];
  }
  if (stateOfMatter?.includes(StateOfMatter.LIQUID)) {
    return [UnitType.GALLONS];
  }
  if (stateOfMatter?.includes(StateOfMatter.GAS)) {
    return [UnitType.GALLONS, UnitType.CUBIC_FEET];
  }
  return [];
}

/**
 * T2S/E-Plan LEPC portals that require pounds and a density for conversion,
 * unlike the rest of the T2S/E-Plan states which accept mixed units.
 */
export const T2S_POUNDS_ONLY_UNIT_STATES = ["FL", "AK", "VA"];

/**
 * States that require every storage location's reported amount in pounds.
 * Note this is a *different* and narrower list than the RFC-level rule above:
 * a state absent from here places no unit constraint on storage locations at
 * all.
 */
export const STATES_REQUIRE_POUNDS_REPORTING_STORAGE_LOCATIONS = [
  ...T2S_POUNDS_ONLY_UNIT_STATES,
  "AZ",
  "CT", // include CT to maintain this change https://github.com/Encamp/ash/pull/15031
  "DE",
  "IL",
  "IN",
  "KS",
  "KY",
  "LA",
  "MA",
  "MD",
  "MI",
  "MN",
  "MO",
  "NE",
  "NJ",
  "ND",
  "NV",
  "PA",
  "TX",
  "WA",
  "WI",
  "WV",
] as const;

function isKnownState(state: string | null | undefined): state is StateAbbreviation {
  if (!state) {
    return false;
  }
  return STATES.some((known) => known === state);
}

/**
 * The units this jurisdiction permits in `ReportingFacilityChemical.unit`, or
 * `null` when it places no constraint.
 *
 * `null` is returned for a state the compliance engine's own jurisdiction
 * scoping would not match — an empty state, or one outside `STATES`, since the
 * default pounds-only rule is scoped with `allStatesExcept(...)` over exactly
 * that list. Returning `null` rather than pounds-only there matters: it is the
 * difference between "this jurisdiction wants pounds" and "we do not know what
 * this jurisdiction wants", and only the first should ever refuse a unit.
 */
export function allowedReportingFacilityChemicalUnits({
  state,
  stateOfMatter,
}: {
  state: string | null | undefined;
  stateOfMatter?: readonly StateOfMatter[] | null;
}): UnitType[] | null {
  if (!isKnownState(state)) {
    return null;
  }
  if (state === "CA") {
    return CA_ALLOWED_REPORTING_UNITS;
  }
  if (state === "OR") {
    return oregonAllowedReportingUnits(stateOfMatter);
  }
  if (state === "SD") {
    return SD_ALLOWED_REPORTING_UNITS;
  }
  return POUNDS_ONLY_REPORTING_UNITS;
}

/**
 * The units this jurisdiction permits on each
 * `ReportingFacilityChemicalStorageLocation`, or `null` when it places no
 * constraint.
 */
export function allowedReportingFacilityChemicalStorageLocationUnits(
  state: string | null | undefined
): UnitType[] | null {
  if (!state) {
    return null;
  }
  const requiresPounds = STATES_REQUIRE_POUNDS_REPORTING_STORAGE_LOCATIONS.some(
    (poundsOnly) => poundsOnly === state
  );
  return requiresPounds ? POUNDS_ONLY_REPORTING_UNITS : null;
}

/**
 * The units a staff reporting-unit override may choose from in this
 * jurisdiction: the intersection of the RFC-level rule and the
 * storage-location rule, because the override stamps its unit onto the parent
 * *and* every child row.
 *
 * `null` means neither level constrains the unit.
 *
 * The intersection is deliberate rather than decorative even though no state
 * currently has a storage-location rule tighter than its RFC-level one (every
 * state in `STATES_REQUIRE_POUNDS_REPORTING_STORAGE_LOCATIONS` is pounds-only
 * at the RFC level too, so the intersection equals the RFC-level answer
 * today). Either list can change independently, and an override that satisfied
 * only one of them would file a child row in a unit its jurisdiction rejects.
 */
export function allowedReportingUnitsForOverride({
  state,
  stateOfMatter,
}: {
  state: string | null | undefined;
  stateOfMatter?: readonly StateOfMatter[] | null;
}): UnitType[] | null {
  const rfcUnits = allowedReportingFacilityChemicalUnits({ state, stateOfMatter });
  const storageLocationUnits = allowedReportingFacilityChemicalStorageLocationUnits(state);

  if (!rfcUnits) {
    return storageLocationUnits;
  }
  if (!storageLocationUnits) {
    return rfcUnits;
  }
  return rfcUnits.filter((unit) => storageLocationUnits.includes(unit));
}
